// Foundations
import { Component, HostBinding, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, iif } from 'rxjs';
import { finalize, map, skip, switchMap, take } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';

// Service
import { PackageService } from '../../services/package.service';
import { AnalyticsService } from '../../services/analytics.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { PaymentService } from 'src/app/services/payment.service';
import { Order, OrderPayment } from 'src/app/services/graphql.service';

// Components
import { ConfirmDialogComponent } from '../../components/_share/confirm-dialog/confirm-dialog.component';

// Models
import { FranchiseLocation } from '../../models/franchise.model';
import { PaymentCreditCard } from 'src/app/models/payment/payment-card.model';
import { PurchaseInfo } from 'src/app/models/package/purchase.model';
import { OrderRequestItem } from 'src/app/models/package/order-request.model';

import * as lodash from 'lodash';

@Component({
  selector: 'app-package-page',
  templateUrl: './package-page.component.html',
  styleUrls: ['./package-page.component.scss'],
})
export class PackagePageComponent implements OnInit {
  // #region -> Property <-
  // ----------

  @HostBinding('class') class = 'page-config';

  /**
   * The "step" indicator for all package related pages.
   *
   * Steps
   * - `0` - Package List page
   * - `1` - Location Selection page
   * - `2` - Location Search page
   * - `3` - Order Summary page
   * - `4` - Checkout page (before purchase)
   * - `5` - Thank You page (after purchase)
   * - `6` - Payment Selection page (after purchase)
   *
   */
  step = 0;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property - Payment <-
  // ----------

  primaryPaymentCreditCard$ = new BehaviorSubject<PaymentCreditCard>(null);
  otherPaymentCreditCardList$ = new BehaviorSubject<PaymentCreditCard[]>([]);

  purchaseInfo: PurchaseInfo = {
    paymentCard: null,
    newCard: false,
    saveNewCard: false,
  };
  purchaseInfo$ = new BehaviorSubject<PurchaseInfo>(this.purchaseInfo);

  // Selected "saved" payment card
  selectedPaymentCreditCard: PaymentCreditCard;

  completedOrderId: string;

  startingLocation: any;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(
    private titleService: Title,
    private packageService: PackageService,
    public dialog: MatDialog,
    private loadingIndicatorService: LoadingIndicatorService,
    private paymentService: PaymentService,
    private _analytics: AnalyticsService
  ) {
    this.titleService.setTitle('Client Portal - Packages');
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Lifecycle <-
  // ----------

  ngOnInit(): void {
    //
    this._analytics.logScreen('packagePage');
    this._prepData();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Page Switching <-
  // ----------

  handlePageContent(step: number): void {
    const lastStep = this.step;
    this.step = step;

    /*
      Take actions based on diff. "steps"
    */
    switch (step) {
      case 0: // Package List page
        this._analytics.logButtonTap('package_gotoPackageList');
        break;
      case 1: // Location Selection page
        // Nothing to do...
        break;
      case 2: // Location SeSearch page
        // Nothing to do...
        break;
      case 3: // Order Summary page
        this._analytics.logButtonTap('package_gotoOrderSummary');
        break;
      case 4: // Checkout page
        // clear orderID
        this.packageService.updateOrderId(null);
        this._analytics.logButtonTap('package_gotoOrderCheckout');
        break;
      case 5: // Thank you page
        this._analytics.logButtonTap('package_confirmPurchase');
        break;
      case 6: // Payment selection page
        break;
    }
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method <-
  // ----------

  handleLocationChange(newLocation: FranchiseLocation) {
    // only show popup if location has changed
    if (
      newLocation.name !==
      this.packageService.orderRequest.selectedLocation.name
    ) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'general-dialog',
        data: {
          title: 'Are you sure you want to change the location?',
          content:
            'Changing to a new location will reset the current selection of packages. ',
          cancelText: 'Cancel',
          okText: 'Change',
          okFn: () => {
            dialogRef.close();
            this.packageService.updateSelectedLocation(newLocation);

            // Re-fetch the data
            this.loadingIndicatorService.show();
            this.packageService
              .fetchAvailablePackages(newLocation)
              .pipe(finalize(() => this.loadingIndicatorService.hide()))
              .subscribe(() => {
                //
                this.loadingIndicatorService.hide();
              });
            this.step = 0;
          },
        },
        autoFocus: false,
      });
    }
    // goto step 0 if the same location was selected
    else {
      this.step = 0;
    }
  }

  handleOrderRequestChange(newRequest: OrderRequestItem) {
    //
    this.packageService.updateOrderRequest(newRequest);
  }

  handleSelectPaymentCreditCard(newCard: PaymentCreditCard) {
    //
    this.selectedPaymentCreditCard = lodash.cloneDeep(newCard);

    // Also update the "purchase info"
    this.purchaseInfo = {
      ...this.purchaseInfo,
      ...{
        paymentCard: newCard,
        newCard: false,
        saveNewCard: false,
      },
    };
    this.purchaseInfo$.next(this.purchaseInfo);
  }

  handlePurchaseInfoChange(newPurchaseInfo: PurchaseInfo) {
    //
    this.purchaseInfo = { ...this.purchaseInfo, ...newPurchaseInfo };
    this.purchaseInfo$.next(this.purchaseInfo);
  }

  handleMakePurchase(event) {
    this.loadingIndicatorService.show();
    iif(
      () => this.packageService.orderRequest.orderId,
      this.paymentService.makePayment(
        this.packageService.orderRequest,
        this.purchaseInfo
      ),
      this.packageService.addOrder().pipe(
        switchMap((newOrder: Order) => {
          this.packageService.updateOrderId(newOrder.id);

          // Go payment
          return this.paymentService.makePayment(
            this.packageService.orderRequest,
            this.purchaseInfo
          );
        })
      )
    )
      .pipe(finalize(() => this.loadingIndicatorService.hide()))
      .subscribe((order: OrderPayment[]) => {
        // Keep the "order ID" from the first item, which should be the non-tip item
        this.completedOrderId = order[0].order.id;

        // Clear the "order request" info
        this.packageService.updateOrderRequest(new OrderRequestItem({}));

        // Move on to the "thank you" page
        this.handlePageContent(5);
      });
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  private _prepData(): void {
    //
    this.loadingIndicatorService.show();
    this.packageService
      .initPackageData()
      .pipe(switchMap(() => this._fetchPaymentCreditCards()))
      .subscribe(() => {
        //
        this.loadingIndicatorService.hide();
      });
  }

  _fetchPaymentCreditCards() {
    //
    this.paymentService.getPaymentCreditCards().subscribe();
    return this.paymentService.paymentCreditCards$.pipe(
      skip(1),
      take(1),
      map((paymentCreditCards) => {
        const processed = this.paymentService.processPaymentCreditCardList(
          paymentCreditCards,
          true
        );
        this.primaryPaymentCreditCard$.next(processed.primary);
        this.otherPaymentCreditCardList$.next(processed.others);

        // Pick the "first" valid card as the default method
        this.selectedPaymentCreditCard =
          this.paymentService.getFirstValidPaymentCard();

        // Check if can mark the "primary" as "selected" payment
        if (this.selectedPaymentCreditCard) {
          //
          this.purchaseInfo = {
            ...this.purchaseInfo,
            ...{
              paymentCard: lodash.cloneDeep(this.selectedPaymentCreditCard),
              newCard: false,
              saveNewCard: false,
            },
          };
          this.purchaseInfo$.next(this.purchaseInfo);
        }
      })
    );
  }

  // ----------
  // #endregion

  /////////////////////////////
}
